import React, { useContext } from 'react';
import { useSubscriptionsAndAllowedFeaturesContext } from 'contexts/SubscriptionsAndAllowedFeaturesContext/SubscriptionsAndAllowedFeaturesContext';
import { usePricingCatalogs } from 'hooks/usePricingCatalogs/usePricingCatalogs';
import { Languages } from '@landr/pricing';
import { i18n } from 'utils/setupTranslations';
import useUpsellActionsProvider from './useUpsellActionsProvider';
import { defaultUpsellActionsContext } from './default';
import { UpsellActionsProviderProps, UpsellActionsContextType } from './types';

const LocaleToLanguage: { [key: string]: Languages } = {
    en: Languages.English,
    fr: Languages.French,
};

export const UpsellActionsContext =
    React.createContext<UpsellActionsContextType>(defaultUpsellActionsContext);

export const UpsellActionsProvider = ({
    children,
    offeredPlanCodes,
    creditsNeeded,
}: UpsellActionsProviderProps): JSX.Element => {
    const { hasStudioPlan, hasSamplesPlan } =
        useSubscriptionsAndAllowedFeaturesContext();

    const { samplesCatalog, studioCatalog, catalogSale, getDiscountedPlan } =
        usePricingCatalogs({
            includePrismicContent: true,
            language: LocaleToLanguage[i18n.locale] ?? Languages.English,
        });

    const context = useUpsellActionsProvider({
        offeredPlanCodes,
        creditsNeeded,
        samplesCatalog,
        studioCatalog,
        catalogSale,
        getDiscountedPlan,
    });

    return (
        <UpsellActionsContext.Provider
            value={{ ...context, hasStudioPlan, hasSamplesPlan }}
        >
            {children}
        </UpsellActionsContext.Provider>
    );
};

export const useUpsellActionsContext = (): UpsellActionsContextType =>
    useContext(UpsellActionsContext);

export default UpsellActionsProvider;
